import { BUTTON_SIZE, BUTTON_TYPE, COLORS, DSButton } from '@repo/design-system/index'
import { QIcon_ai_tutor, QIcon_clock, QIcon_close_default, QIcon_study_club, QIcon_ticket } from '@repo/icons/index'
import { format, parseISO } from 'date-fns'
import { useState } from 'react'
import { GTM_CLICK_EVENT, useEventTracking } from '../../../../helpers/gtmHelper'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsInApp from '../../../../modules/hooks/common/useIsInApp'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import usePromotion from '../../../../modules/hooks/usePromotion'
import useReturnFullUrl from '../../../../modules/hooks/webview/common/useReturnFullUrl'
import {
  n_pricing,
  n_purchase,
  s_certificate,
  s_landing_pricing,
  s_portal_home,
  s_pricing,
} from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import urls from '../../../../modules/urls'
import { isFirstBenefitUser } from '../../../../modules/utils'
import BottomSheet from './BottomSheet'
import { PackageItemBottomSheet, PackageItemModal } from './PackageDialog'
import { productTypeEnum, useProductContext } from './ProductContext'

interface PackageOptionCardProps {
  product: any
  isB2B?: boolean
  duration?: number
  currency?: string
  companyEducationId?: number
  isLowestPrice?: boolean
}

//🧩 this is for b2c, b2b2c, not for b2b2c_hr, not for b2bhr, not for b2b2c_hr_round.
const PackageOptionCard = ({
  product,
  isB2B = false,
  duration,
  currency,
  companyEducationId,
  isLowestPrice,
}: PackageOptionCardProps) => {
  const { t, isLocaleKO } = useTranslation()
  const isMobile = useIsMobile()
  const router = useAppRouter()
  const isInApp = useIsInApp()
  const isWebview = useIsWebview()
  const getFullUrl = useReturnFullUrl()
  const isApp = router.query.app ? true : false
  const { currentUser } = useCurrentUser()
  const { sendClickEvent } = useEventTracking()
  const { isPromotionPeriod } = usePromotion()

  const isFirstPurchaseBenefitUser = isFirstBenefitUser(currentUser)

  const { scrollTargetRef, productType } = useProductContext()
  const isOneOnOne = productType.type === productTypeEnum.PERSONAL_LESSON

  const [isOpen, setIsOpen] = useState(null)

  const generateDiscountLabel = (amount: number) => {
    const discountWonKo = amount / 10000
    // const discountWonEn = amount / 1000
    const discountDollar = amount

    return isLocaleKO ? `이벤트 ${discountWonKo}만원 추가 할인` : t(n_pricing.b2b2c_label.sale(discountDollar))
  }

  const currentProduct = isB2B ? product.flexibleLesson : product.weeklyLesson

  const navigateToPurchase = () => {
    let url = ''
    if (companyEducationId) {
      url = `${urls.student.newPortal.pricing.detail(
        currentProduct.id
      )}?country_code=KR&app=${isApp}&company_education_id=${companyEducationId}`
    } else {
      url = `${urls.student.newPortal.pricing.detail(currentProduct.id)}?country_code=KR&app=${isApp}`
    }

    if (isWebview && !isInApp) {
      router.push(url, {
        appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
        appSendData: { url: getFullUrl(url) },
      })
    } else {
      router.push(url)
    }
  }

  const handlePurchaseClick = () => {
    isB2B ? navigateToPurchase() : setIsOpen(true)
  }

  const { prices, properties, bonuses, discounts } = currentProduct

  const isUSDAmount = currency === 'USD' || currency === 'US'
  const isKrwAmount = currency === 'KRW' || currency === 'KR'
  const isKrwUsdAmount = isUSDAmount || isKrwAmount

  const isB2B2CHRRound = !!currentProduct?.company_education?.id
  const roundStartDate = isB2B2CHRRound ? currentProduct?.company_education?.start_date : null
  const roundStartDateLabel = roundStartDate ? format(parseISO(roundStartDate), 'yy.MM.dd') : ''
  const roundEndDate = isB2B2CHRRound ? currentProduct?.company_education?.end_date : null
  const roundEndDateLabel = roundEndDate ? format(parseISO(roundEndDate), 'yy.MM.dd') : ''

  const eventDiscountValue = discounts.find(
    (discount) => discount.title === '이벤트 할인' || discount.title === 'Event Discount'
  )?.amount?.value
  const discountedFromOriginalPrice = isLocaleKO
    ? generateDiscountLabel(bonuses.additional_discount_label_only)
    : generateDiscountLabel(bonuses.additional_discount_label_only_dollar)

  const hasStudyClub = bonuses.study_club_benefit

  const additionalDiscountAmount =
    bonuses?.additional_discount_label_only > 0
      ? isLocaleKO
        ? (+bonuses.additional_discount_label_only)?.toLocaleString()
        : bonuses.additional_discount_label_only_dollar
      : null

  const isTwelveWeekPackage = currentProduct?.exp_weeks === 12

  const durationPackageItemTitle =
    duration === 20 ? t(n_pricing.lesson_option.twenty) : t(n_pricing.lesson_option.forty)

  const [isPackageDetailOpen, setIsPackageDetailOpen] = useState(false)

  if (isMobile) {
    return (
      <>
        <button
          ref={isTwelveWeekPackage ? scrollTargetRef : null}
          className="relative flex p-16 w-full flex-col gap-y-4 rounded-[8px] border"
          onClick={() => {
            setIsPackageDetailOpen(true)
            sendClickEvent({ button_name: GTM_CLICK_EVENT.PURCHASE_CARD_MOBILE })
          }}
        >
          <div className="absolute -top-10 left-8 flex gap-x-4">
            {properties.is_popular && (
              <BenefitBadge title={t(n_pricing.label.recommended)} className="bg-purple-500 text-white" />
            )}
            {isLowestPrice && (
              <BenefitBadge title={t(n_pricing.label.lowest)} className="bg-green-50 text-green-500 border-green-200" />
            )}
            {/* {currentProduct.first_purchase_benefit > 0 && isFirstPurchaseBenefitUser && ( */}
            {isFirstPurchaseBenefitUser && (
              <BenefitBadge
                icon={<QIcon_clock width={12} height={12} color={COLORS.RED500} />}
                title={t(n_pricing.label.first_purchase)}
                className="bg-red-50 text-red-500 border-red-200"
              />
            )}
            {!isB2B && eventDiscountValue > 0 && (
              <BenefitBadge title={discountedFromOriginalPrice} className="bg-blue-50 text-blue-500 border-blue-200" />
            )}
          </div>

          <div className="w-full flex justify-start">
            <span className="Body1-16Bd">{currentProduct.name}</span>
          </div>

          {isOneOnOne && (
            <div className="w-full flex flex-col items-end">
              <div className="flex items-center gap-x-4">
                {currentProduct.discount_rate > 0 && (
                  <span className="Caption-12Bd text-red-400">{currentProduct.discount_rate}%↓</span>
                )}
                <span className="Title2-18Bd">{prices.price_per_lesson.formatted}</span>
                <span className="Title2-18Bd">/ {t(s_landing_pricing.time)}</span>
              </div>
              <div className="flex gap-x-4">
                {currentProduct.total_discount_amount.value > 0 && (
                  <span className="Body2-14Md text-gray-400 line-through decoration-red-500">
                    {prices.list_price.formatted}
                  </span>
                )}
                <span className="Body2-14Md text-gray-600">{prices.sale_price.formatted}</span>
              </div>
            </div>
          )}
          {!isOneOnOne && (
            <div className="w-full flex flex-col items-end">
              <div className="flex items-center gap-x-4">
                {currentProduct.discount_rate > 0 && (
                  <span className="Caption-12Bd text-red-400">{currentProduct.discount_rate}%↓</span>
                )}
                {isLocaleKO && <span className="Body2-14Bd">4주</span>}
                <span className="Title2-18Bd">{prices.price_per_four_weeks.formatted}</span>
                {!isLocaleKO && <span className="Body2-14Bd">/4 weeks</span>}
              </div>
              <div className="flex gap-x-4">
                {currentProduct.total_discount_amount.value > 0 && (
                  <span className="Body2-14Md text-gray-400 line-through decoration-red-500">
                    {prices.list_price.formatted}
                  </span>
                )}
                <span className="Body2-14Md text-gray-600">{prices.sale_price.formatted}</span>
              </div>
              {!isKrwUsdAmount && (
                <span className="text-14 text-gray-500">({prices.price_per_four_weeks.equivalent?.formatted})</span>
              )}
            </div>
          )}
        </button>

        <BottomSheet isOpen={isPackageDetailOpen} onClose={() => setIsPackageDetailOpen(false)}>
          <div className="px-20 flex flex-col">
            <section className="pb-20 flex justify-between items-start sticky top-0 bg-white z-10">
              <div className="flex flex-col gap-y-16">
                <div className="flex flex-wrap gap-x-4">
                  {isLowestPrice && (
                    <BenefitBadge
                      title={t(n_pricing.label.lowest)}
                      className="bg-green-50 text-green-500 border-green-200"
                    />
                  )}
                  {/* {currentProduct.first_purchase_benefit > 0 && isFirstPurchaseBenefitUser && ( */}
                  {isFirstPurchaseBenefitUser && (
                    <BenefitBadge
                      icon={<QIcon_clock width={12} height={12} color={COLORS.RED500} />}
                      title={t(n_pricing.label.first_purchase)}
                      className="max-w-[110px] bg-red-50 text-red-500 border-red-200"
                    />
                  )}
                  {!isB2B && eventDiscountValue > 0 && (
                    <BenefitBadge
                      title={discountedFromOriginalPrice}
                      className="bg-blue-50 text-blue-500 border-blue-200"
                    />
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="Headline2-24Bd ">{currentProduct.name}</span>
                  <span className="Body2-14Md text-gray-500">
                    {t(n_pricing.lesson_span)} {currentProduct.exp_dates}
                    {t(s_certificate.days)}
                  </span>
                </div>
              </div>
              <button onClick={() => setIsPackageDetailOpen(false)}>
                <QIcon_close_default width={24} height={24} />
              </button>
            </section>
            <section className="flex flex-col gap-y-8">
              <PackageItem
                title={durationPackageItemTitle}
                icon={
                  <img
                    src={'https://d38emex6h5e12i.cloudfront.net/2024-image/1718603214332_tutor_icon_gray.webp'}
                    alt="lesson_icon"
                    className="w-24 h-24"
                  />
                }
                baseCount={t(n_pricing.credit_number_count(currentProduct.coupon_count))}
                additionalCount={
                  (isFirstPurchaseBenefitUser && 1) || (isB2B && currentProduct.bonuses.completion_bonus)
                }
                // additionalCount={
                //   (isFirstPurchaseBenefitUser && currentProduct.first_purchase_benefit) ||
                //   (isB2B && currentProduct.bonuses.completion_bonus)
                // }
                benefit={bonuses.booking_priority_benefit && t(n_pricing.package.book_priority)}
                isB2B={isB2B}
              />

              {currentProduct.ai_tutor_exp_dates && (
                <>
                  <PackageItem
                    title={t(n_pricing.package.ai_premium)}
                    icon={<QIcon_ai_tutor width={24} height={24} color={COLORS.GRAY400} />}
                    baseCount={t(s_portal_home.default_stat.week(currentProduct.ai_tutor_exp_weeks))}
                  />
                  <PackageItem
                    title={
                      <div className="flex gap-x-2 items-center">
                        <span>{t(n_purchase.benefit.ai_phone_call)}</span>
                        <span className="Overline-10Md text-gray-600 mb-4">{t(n_purchase.benefit.beta)}</span>
                      </div>
                    }
                    icon={<QIcon_ai_tutor width={24} height={24} color={COLORS.GRAY400} />}
                    baseCount={t(s_portal_home.default_stat.week(currentProduct.ai_tutor_exp_weeks))}
                  />
                </>
              )}
              {/* {hasCareerWebinar && (
                <>
                  <PackageItem
                    title={isLocaleKO ? '커리어 웨비나 (30개)' : '30 Career Webinars'}
                    icon={<QIcon_video width={24} height={24} color={COLORS.GRAY400} />}
                  />
                  <PackageItem
                    title={t(['커리어 Live 신청권 (2개)', '2 Career Live Passes'])}
                    icon={<QIcon_ticket width={24} height={24} color={COLORS.GRAY400} />}
                  />
                </>
              )}
              {hasInterviewMaterials && (
                <PackageItem
                  title={t(['인터뷰 자료집', 'Interview Handbook'])}
                  icon={
                    <div className="p-2">
                      <QIcon_exclude width={20} height={20} color={COLORS.GRAY400} />
                    </div>
                  }
                />
              )} */}
              {currentProduct?.exp_weeks >= 12 && (
                <PackageItem
                  title={t(n_purchase.benefit.webinar_ticket)}
                  icon={<QIcon_ticket width={24} height={24} color={COLORS.GRAY400} />}
                />
              )}
              {hasStudyClub && !isB2B && (
                <PackageItem
                  title={t(n_pricing.benefit.study_club_plain)}
                  icon={<QIcon_study_club width={24} height={24} color={COLORS.GRAY400} />}
                />
              )}
            </section>
            <div className="sm:static sticky bottom-0 left-0 pb-36 sm:pb-0 pt-12 w-full flex justify-end bg-white">
              <DSButton
                className="sm:w-1/3 w-full"
                size={BUTTON_SIZE.LG}
                customBgColor={COLORS.PURPLE600}
                onClick={() => {
                  setIsPackageDetailOpen(false)
                  setIsOpen(true)
                  sendClickEvent({ button_name: GTM_CLICK_EVENT.PURCHASE_CARD_BTN_MOBILE })
                }}
              >
                <DSButton.Label>{t(s_pricing.buy_btn)}</DSButton.Label>
              </DSButton>
            </div>
          </div>
        </BottomSheet>
        <PackageItemBottomSheet product={product} isOpen={isOpen} setIsOpen={setIsOpen} />
      </>
    )
  }

  return (
    <section
      ref={isTwelveWeekPackage ? scrollTargetRef : null}
      className="min-w-[280px] sm:max-w-[280px] w-full sm:w-auto sm:h-full sm:min-h-[636px] flex flex-col gap-y-32 bg-white border rounded-lg shadow"
    >
      <header className="flex flex-col gap-y-20">
        <div className={`sm:h-28 rounded-t-lg text-center overflow-hidden`}>
          {properties.is_popular && (
            <div className={`py-2 bg-purple-500`}>
              <span className="text-white Body2-14Md"> {t(n_pricing.label.recommended)}</span>
            </div>
          )}
          {!properties.is_popular && isLowestPrice && (
            <div className={`py-2 bg-green-300`}>
              <span className="text-white Body2-14Md"> {t(n_pricing.label.lowest)}</span>
            </div>
          )}
        </div>

        <div className="px-16 flex flex-col gap-y-16">
          <div className="flex flex-wrap gap-4 min-h-[25px]">
            {/* {currentProduct.first_purchase_benefit > 0 && isFirstPurchaseBenefitUser && ( */}
            {isFirstPurchaseBenefitUser && (
              <BenefitBadge
                icon={<QIcon_clock width={12} height={12} color={COLORS.RED500} />}
                title={t(n_pricing.label.first_purchase)}
                className="max-w-[110px] bg-red-50 text-red-500 border-red-200"
              />
            )}
            {!isB2B && eventDiscountValue > 0 && (
              <BenefitBadge title={discountedFromOriginalPrice} className="bg-blue-50 text-blue-500 border-blue-200" />
            )}
          </div>
          <div className="flex flex-col">
            <span className="Headline3-22Bd text-black">{currentProduct.name}</span>
            {!isB2B && (
              <span className="Body2-14Md text-gray-500">
                {t(n_pricing.lesson_span)} {currentProduct.exp_dates}
                {t(s_certificate.days)}
              </span>
            )}
            {isB2B && isB2B2CHRRound && (
              <span className="Body2-14Md text-gray-500">
                {t(n_pricing.period)} {roundStartDateLabel} ~ {roundEndDateLabel}
              </span>
            )}
          </div>
        </div>
      </header>
      <main className="px-16 flex flex-col gap-y-32 sm:flex-1">
        <section className="w-full flex flex-col items-end">
          {isOneOnOne && (
            <>
              <div className="flex gap-x-8">
                {currentProduct.total_discount_amount.value > 0 && (
                  <span className="Body2-14Md text-gray-400 line-through decoration-red-500">
                    {prices.list_price.formatted}
                  </span>
                )}
                <span className="Body2-14Md text-gray-600">{prices.sale_price.formatted}</span>
              </div>
              <div className="flex gap-x-6 items-center">
                {currentProduct.discount_rate > 0 && (
                  <span className="Body1-16Bd text-red-500">{currentProduct.discount_rate}%↓</span>
                )}

                <span className="Headline3-22Bd">{prices.price_per_lesson.formatted}</span>
                <span className="Body1-16Bd">/ {t(s_landing_pricing.time)}</span>
              </div>

              {/* FIXME: 회당 단가 다국적 통화 서버 작업 완료 후 변경 필요 */}
              {/* {!isKrwUsdAmount && (
                <span className="text-14 text-gray-500">({prices.price_per_four_weeks.equivalent?.formatted})</span>
              )} */}
            </>
          )}
          {!isOneOnOne && (
            <>
              <div className="flex gap-x-8">
                {currentProduct.total_discount_amount.value > 0 && (
                  <span className="Body2-14Md text-gray-400 line-through decoration-red-500">
                    {prices.list_price.formatted}
                  </span>
                )}
                <span className="Body2-14Md text-gray-600">{prices.sale_price.formatted}</span>
              </div>
              <div className="flex gap-x-6 items-center whitespace-pre">
                {currentProduct.discount_rate > 0 && (
                  <span className="Body1-16Bd text-red-500">{currentProduct.discount_rate}%↓</span>
                )}
                {isLocaleKO && <span className="Body1-16Bd">4주</span>}
                <span className="Headline3-22Bd">{prices.price_per_four_weeks.formatted}</span>
                {!isLocaleKO && <span className="Body1-16Bd">/4 weeks</span>}
              </div>

              {!isKrwUsdAmount && (
                <span className="text-14 text-gray-500">({prices.price_per_four_weeks.equivalent?.formatted})</span>
              )}
            </>
          )}
        </section>

        <section className="flex flex-col gap-y-8">
          <PackageItem
            title={durationPackageItemTitle}
            icon={
              <img
                src={'https://d38emex6h5e12i.cloudfront.net/2024-image/1718603214332_tutor_icon_gray.webp'}
                alt="lesson_icon"
                className="w-24 h-24"
              />
            }
            baseCount={`${t(n_pricing.credit_number_count(currentProduct.coupon_count))}`}
            additionalCount={(isFirstPurchaseBenefitUser && 1) || (isB2B && currentProduct.bonuses.completion_bonus)}
            // additionalCount={
            //   (isFirstPurchaseBenefitUser && currentProduct.first_purchase_benefit) ||
            //   (isB2B && currentProduct.bonuses.completion_bonus)
            // }
            benefit={bonuses.booking_priority_benefit && t(n_pricing.package.book_priority)}
            isB2B={isB2B}
          />
          {currentProduct.ai_tutor_exp_dates && (
            <>
              <PackageItem
                title={t(n_pricing.package.ai_premium)}
                icon={<QIcon_ai_tutor width={24} height={24} color={COLORS.GRAY400} />}
                baseCount={t(s_portal_home.default_stat.week(currentProduct.ai_tutor_exp_weeks))}
              />
              <PackageItem
                title={
                  <div className="flex gap-x-2 items-center">
                    <span>{t(n_purchase.benefit.ai_phone_call)}</span>
                    <span className="Overline-10Md text-gray-600 mb-4">{t(n_purchase.benefit.beta)}</span>
                  </div>
                }
                icon={<QIcon_ai_tutor width={24} height={24} color={COLORS.GRAY400} />}
                baseCount={t(s_portal_home.default_stat.week(currentProduct.ai_tutor_exp_weeks))}
              />
            </>
          )}
          {/* {hasCareerWebinar && (
            <>
              <PackageItem
                title={isLocaleKO ? '커리어 웨비나 (30개)' : '30 Career Webinars'}
                icon={<QIcon_video width={24} height={24} color={COLORS.GRAY400} />}
              />
              <PackageItem
                title={t(['커리어 Live 신청권 (2개)', '2 Career Live Passes'])}
                icon={<QIcon_ticket width={24} height={24} color={COLORS.GRAY400} />}
              />
            </>
          )}
          {hasInterviewMaterials && (
            <PackageItem
              title={t(['인터뷰 자료집', 'Interview Handbook'])}
              icon={
                <div className="p-2">
                  <QIcon_exclude width={20} height={20} color={COLORS.GRAY400} />
                </div>
              }
            />
          )} */}
          {currentProduct?.exp_weeks >= 12 && (
            <PackageItem
              title={t(n_purchase.benefit.webinar_ticket)}
              icon={<QIcon_ticket width={24} height={24} color={COLORS.GRAY400} />}
            />
          )}
          {hasStudyClub && !isB2B && (
            <PackageItem
              title={t(n_pricing.benefit.study_club_plain)}
              icon={<QIcon_study_club width={24} height={24} color={COLORS.GRAY400} />}
            />
          )}
          <div></div>
        </section>
      </main>

      <footer className="px-16 pb-32">
        <DSButton
          className=""
          type={BUTTON_TYPE.SOLID}
          size={BUTTON_SIZE.MD}
          customBgColor={COLORS.PURPLE500}
          onClick={() => handlePurchaseClick()}
        >
          <DSButton.Label>{t(n_pricing.button.purchase)}</DSButton.Label>
        </DSButton>
      </footer>

      {isMobile ? (
        <PackageItemBottomSheet product={product} isOpen={isOpen} setIsOpen={setIsOpen} />
      ) : (
        <PackageItemModal product={product} isOpen={isOpen} setIsOpen={setIsOpen} />
      )}
    </section>
  )
}

export default PackageOptionCard

export const BenefitBadge = ({ title, icon = null, className }) => {
  return (
    <div className={`py-2 px-6 self-start inline-flex gap-x-4 items-center border rounded ${className}`}>
      {icon}
      <span className={`sm:Caption-12Bd Overline-10Bd whitespace-nowrap`}>{title}</span>
    </div>
  )
}

const PackageItem = ({ title, icon, baseCount = null, additionalCount = null, benefit = null, isB2B = false }) => {
  const { t, isLocaleKO } = useTranslation()
  const { sendClickEvent } = useEventTracking()

  const handleItemClick = () => {
    sendClickEvent({ button_name: 'click_purchase_detail' })
  }

  return (
    <div className="p-12 flex flex-col gap-y-8 bg-gray-50 rounded-lg" onClick={handleItemClick}>
      <div className="flex justify-between items-center">
        <div className="flex gap-x-8 items-center">
          {icon}
          <span className="text-gray-700 Body2-14Md">{title}</span>
        </div>
        <div className={`flex gap-x-4 ${isLocaleKO ? '' : 'flex-col items-start'}`}>
          {!!baseCount && <span className="self-end Body2-14Bd whitespace-pre">{baseCount}</span>}
          {!!additionalCount && (
            <div className="flex gap-x-2">
              <span className={`Body2-14Bd ${isB2B ? 'text-blue-500' : 'text-red-500'} whitespace-pre`}>
                + {t(n_pricing.credit_number_additional_count(additionalCount))}
              </span>
            </div>
          )}
        </div>
      </div>
      {benefit && <span className="Body2-14Md text-purple-400">{benefit}</span>}
    </div>
  )
}
